import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { EquipmentOwnership, EquipmentType, FleetType } from "Util/enums";
import * as yup from "yup";

import "@fontsource/manrope";
import UITabMaterial, { PanelProps } from "../../../../../../../components/Generic/UITabMaterial";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Fleet } from "../../../../../../../models/fleet";
import { BreadcrumbKit } from "../../Subcontractors/Breadcrumb";
import { Details } from "../Edit/Tabs/Details";
import useEquipmentActions from "../hooks/useEquipmentActions";
import { EditEquipmentLoading } from "./components/EditFleetLoading";
import EquipmentEditFooter from "./components/Footer";
import { EquipmentProvider, useEquipmentContext } from "./context/Equipment";
// eslint-disable-next-line import/order
import { currencyToNumber } from "../../../../../../../util/prices";
import useStyles from "./styles";
import { AvailabilityFleetTab } from "./Tabs/AvailabilityFleet";
import { MembersAndTeams } from "./Tabs/MembersAndTeams";
import { Orders } from "./Tabs/Orders";

type Param = {
  equipmentId?: string | undefined;
};

type LocationState = {
  viewOnly?: boolean;
};

const EditPage: React.FC = () => {
  const { equipmentId } = useParams<Param>();
  const routeLocation = useLocation<LocationState>();
  const viewOnly = !!routeLocation?.state?.viewOnly;

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const { equipment, setEquipment } = useEquipmentContext();
  const { getEquipmentById, getFleetTypes, onBackToEquipmentsTab, useSaveEquipment } =
    useEquipmentActions();
  const { data: fleetTypes } = getFleetTypes();

  const { data: equipmentData, isFetching: loadingEquipment } = getEquipmentById(
    equipmentId ? Number(equipmentId) : undefined
  );

  const { mutate: saveEquipment, isLoading: isLoadingSaveEquipment } =
    useSaveEquipment(onBackToEquipmentsTab);

  useEffect(() => {
    if (equipmentData) {
      setEquipment((prev) => ({
        ...prev,
        ...equipmentData,
      }));
    }
  }, [equipmentData]);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { someFieldHasError } = useYupValidation(errors);

  const schema = yup.object().shape({
    equipmentType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    branches: yup
      .array()
      .test("empty-check", translate("thisFieldIsRequired"), (fleetBranches) =>
        fleetBranches ? fleetBranches.length > 0 : false
      ),
    warehouseId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    ownership: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    subcontractorId: yup.number().when("ownership", {
      is: (value: Fleet["ownership"]) => value?.toLowerCase() === EquipmentOwnership.SUBCONTRACTOR,
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.number().nullable(),
    }),
    fleetTypeId: yup.number().when("equipmentType", {
      is: (value: Fleet["equipmentType"]) => value === EquipmentType.FLEET,
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.number().nullable(),
    }),
    fleetNumber: yup.string().when("equipmentType", {
      is: (value: Fleet["equipmentType"]) => value === EquipmentType.FLEET,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    ratePerHour: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onValidateFields = () => {
    const data = {
      ...equipment,
      fleetTypeName:
        fleetTypes?.find(
          (type) =>
            equipment.fleetTypeId &&
            type?.id === equipment.fleetTypeId &&
            type?.name?.toLowerCase().includes(FleetType.TRUCK.toLowerCase())
        )?.name ?? "",
    };

    try {
      schema.validateSync(data, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const detailsPanel: PanelProps = {
    menuItem: translate("details"),
    className: classes.uiTab,
    key: "details",
    loading: loadingEquipment,
    skeleton: () => <EditEquipmentLoading style={{ padding: "12px" }} />,
    hasError: someFieldHasError(["fleetTypeId"] as Array<keyof Fleet>),
    render: () => <Details isView={viewOnly} errors={errors} />,
    skipContainerPadding: true,
  };
  const availabilityPanel: PanelProps = {
    menuItem: translate("availability"),
    className: classes.uiTab,
    key: "availability",
    hasError: someFieldHasError(["fleetTypeId"] as Array<keyof Fleet>),
    render: () => <AvailabilityFleetTab isView={viewOnly} />,
  };
  const membersAndTeamsPanel: PanelProps = {
    menuItem: translate("membersAndTeams"),
    className: classes.uiTab,
    key: "membersAndTeams",
    loading: loadingEquipment,
    skeleton: () => <EditEquipmentLoading />,
    hasError: someFieldHasError(["fleetTypeId"] as Array<keyof Fleet>),
    render: () => <MembersAndTeams />,
  };
  const ordersPanel: PanelProps = {
    menuItem: translate("orders"),
    className: classes.uiTab,
    key: "orders",
    loading: loadingEquipment,
    skeleton: () => <EditEquipmentLoading />,
    hasError: someFieldHasError(["fleetTypeId"] as Array<keyof Fleet>),
    render: () => <Orders />,
  };

  const leftPanels: PanelProps[] = [
    detailsPanel,
    availabilityPanel,
    membersAndTeamsPanel,
    ordersPanel,
  ];

  const onSubmit = () => {
    if (onValidateFields()) {
      saveEquipment({
        fleet: {
          ...equipment,
          ratePerHour: equipment?.ratePerHour
            ? currencyToNumber(String(equipment?.ratePerHour))
            : undefined,
        },
        id: equipment?.id || Number(equipmentId),
      });
    }
  };

  return (
    <Box className={classes.Wrapper}>
      <BreadcrumbKit
        items={[
          {
            key: "settings",
            label: translate("settings"),
            onClick: onBackToEquipmentsTab,
          },
          {
            key: "equipment",
            label: translate("equipment"),
            onClick: onBackToEquipmentsTab,
          },
          {
            key: "edit",
            label: `${translate(viewOnly ? "view" : "edit")} ${
              equipment?.fleetNumber || equipment?.description || ""
            }`,
          },
        ]}
      />
      <div className={classes.ordersTab}>
        <UITabMaterial
          leftPanels={leftPanels}
          activityPanels={leftPanels}
          showRightPanel={false}
          showActivityButton={false}
        />
        <EquipmentEditFooter
          isLoading={isLoadingSaveEquipment}
          onCancel={onBackToEquipmentsTab}
          onSave={onSubmit}
          isShowSave={!viewOnly}
        />
      </div>
    </Box>
  );
};

const EquipmentEditMaintenance = () => {
  return (
    <EquipmentProvider>
      <EditPage />
    </EquipmentProvider>
  );
};

export default EquipmentEditMaintenance;
